import React from 'react';
import {
  MarkdownTextarea,
  ModelSelector,
  ParameterControls,
  FileUpload,
  MediaPreview,
  PagedOutput,
  PromptTemplate,
} from './index';
import { ImagePanel } from './ImagePanel';
import { AudioRecorder } from './AudioRecorder';
import { LanguageSelector } from './LanguageSelector';
import { TranscriptionOutput } from './TranscriptionOutput';
import type {
  MarkdownTextareaProps,
  ModelSelectorProps,
  ParameterControlsProps,
  FileUploadProps,
  MediaPreviewProps,
  PagedOutputProps,
  PromptTemplateProps,
} from './index';
import type { AudioRecorderProps } from './AudioRecorder';
import type { LanguageSelectorProps } from './LanguageSelector';
import type { TranscriptionOutputProps } from './TranscriptionOutput';

/**
 * Component Registry
 * Maps component names to their implementations and prop types
 */

export type ComponentType =
  | 'MarkdownTextarea'
  | 'ModelSelector'
  | 'ParameterControls'
  | 'FileUpload'
  | 'MediaPreview'
  | 'PagedOutput'
  | 'ImagePanel'
  | 'PromptTemplate'
  | 'AudioRecorder'
  | 'LanguageSelector'
  | 'TranscriptionOutput';

export type ComponentProps =
  | MarkdownTextareaProps
  | ModelSelectorProps
  | ParameterControlsProps
  | FileUploadProps
  | MediaPreviewProps
  | PagedOutputProps
  | PromptTemplateProps
  | AudioRecorderProps
  | LanguageSelectorProps
  | TranscriptionOutputProps
  | { nodeId: string; value?: string; onChange?: (value: string) => void; disabled?: boolean; targetWidth?: number };

export interface ComponentRegistryEntry {
  component: React.ComponentType<any>;
  defaultProps?: Partial<ComponentProps>;
}

/**
 * Registry mapping component names to their React components
 */
export const COMPONENT_REGISTRY: Record<ComponentType, ComponentRegistryEntry> = {
  MarkdownTextarea: {
    component: MarkdownTextarea,
    defaultProps: {
      variant: 'neutral',
      minHeight: '80px',
      maxHeight: '400px',
      autoFocus: false,
      disabled: false,
      showSubmitButton: false,
      readOnly: false,
    },
  },
  
  ModelSelector: {
    component: ModelSelector,
    defaultProps: {
      variant: 'neutral',
      disabled: false,
    },
  },
  
  ParameterControls: {
    component: ParameterControls,
    defaultProps: {
      variant: 'neutral',
      disabled: false,
    },
  },
  
  FileUpload: {
    component: FileUpload,
    defaultProps: {
      variant: 'neutral',
      disabled: false,
      multiple: false,
    },
  },
  
  MediaPreview: {
    component: MediaPreview,
    defaultProps: {
      variant: 'neutral',
      controls: true,
      autoPlay: false,
      maxHeight: '400px',
      allowFullscreen: true,
    },
  },
  
  PagedOutput: {
    component: PagedOutput,
    defaultProps: {
      variant: 'neutral',
      minHeight: '150px',
      maxHeight: '500px',
      pages: [],
      currentPageIndex: 0,
    },
  },
  
  ImagePanel: {
    component: ImagePanel,
    defaultProps: {
      disabled: false,
      targetWidth: 420,
    },
  },
  
  PromptTemplate: {
    component: PromptTemplate,
    defaultProps: {
      template: '',
      variables: {},
      aiModel: 'gpt-4o-mini',
      hasAdditionalInstructions: false,
      additionalInstructionsLabel: 'Additional Instructions',
      additionalInstructionsValue: '',
      parameters: {},
      disabled: false,
      variant: 'neutral',
      nodeId: '', // Will be overridden by UniversalNode
    },
  },
  
  AudioRecorder: {
    component: AudioRecorder,
    defaultProps: {
      variant: 'positive',
      autoTranscribe: true,
      language: 'en',
      disabled: false,
    },
  },
  
  LanguageSelector: {
    component: LanguageSelector,
    defaultProps: {
      variant: 'positive',
      value: 'en',
      disabled: false,
    },
  },
  
  TranscriptionOutput: {
    component: TranscriptionOutput,
    defaultProps: {
      variant: 'positive',
      value: '',
      placeholder: 'Transcription will appear here...',
      editable: true,
      disabled: false,
    },
  },
};

/**
 * Get a component from the registry by name
 */
export function getComponent(type: ComponentType): ComponentRegistryEntry | null {
  return COMPONENT_REGISTRY[type] || null;
}

/**
 * Render a component from the registry with props
 */
export function renderComponent(
  type: ComponentType,
  props: ComponentProps,
  key?: string | number
): React.ReactElement | null {
  const entry = getComponent(type);
  if (!entry) {
    console.warn(`Component type "${type}" not found in registry`);
    return null;
  }

  const Component = entry.component;
  const mergedProps = { ...entry.defaultProps, ...props };

  return <Component key={key} {...mergedProps} />;
}
